<template>
  <audoora-header v-if="blok.header" :blok="blok.header.content" class="sticky top-0 z-30" />
  <main
    v-editable="blok"
    class="relative isolate z-10 flex min-h-dvh flex-grow flex-col"
    :class="{
      '-mt-28': behindHeader,
      'pt-28': blok.body?.at(0)?.component === 'hero-video',
      '[&>:first-child]:!pt-28': blok.body?.at(0)?.body?.at(0).component === 'hero-video',
    }"
  >
    <multipage-dialog-root>
      <storyblok-component v-for="childBlok in blok.bodyBefore" :key="childBlok._uid" :blok="childBlok" class="z-10" />

      <section class="container relative mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 py-12 tablet:py-16 desktop:grid-cols-4">
        <div class="hidden desktop:block">
          <sb-toc
            :blok="textWithHeadingIds"
            :uid="blok._uid"
            class="sticky top-28 overflow-y-auto rounded border-2 border-text bg-back p-4 shadow desktop:max-h-[calc(100dvh-9.5rem)]"
          />
        </div>
        <div class="inline-images prose prose-lg z-10 col-span-3 mx-auto dark:prose-invert [&_a]:break-all [&_a]:hyphens-auto">
          <runtime-template :template="renderedText" />
        </div>
      </section>

      <storyblok-component v-for="childBlok in blok.bodyAfter" :key="childBlok._uid" :blok="childBlok" class="z-10" />

      <page-flyout v-if="blok.phoneNumber" v-editable="blok" label="Rufen Sie uns an" icon="phone">
        <a class="text-2xl font-semibold" :href="`tel:${blok.phoneNumber}`">{{ blok.phoneNumber }}</a>
        <div class="inline-images prose prose-base ml-auto mt-2 leading-tight dark:prose-invert" v-html="phoneText" />
      </page-flyout>
    </multipage-dialog-root>
  </main>
  <audoora-footer v-if="blok.footer" :blok="blok.footer.content" />
</template>

<script setup lang="ts">
import runtimeTemplate from "vue3-runtime-template"
import type { PageWithTocStoryblok, RichtextStoryblok } from "~/storyblok-component-types"
import { isEditorKey } from "~/injection-symbols"

const props = defineProps<{ blok: withHeaderFooter<PageWithTocStoryblok>; }>()
const isEditor = inject(isEditorKey)
usePageSeo(props.blok)

const phoneText = renderRTF(props.blok?.phoneText)

const textWithHeadingIds = computed<RichtextStoryblok>(() => {
  return typeof props.blok?.text === "string"
    ? props.blok.text
    : {
      ...props.blok.text!,
      content: props.blok.text?.content?.map(cont => ({
        ...cont,
        ...(cont.marks || {
          marks: [
            {
              type: "anchor",
              attrs: {
                id: `h${cont.attrs?.level}-${cont.content?.map((el: any) => el.text).join()}`
                  .toLowerCase()
                  .replaceAll("ä", "ae")
                  .replaceAll("ö", "oe")
                  .replaceAll("ü", "ue")
                  .replaceAll("ß", "ss")
                  .replaceAll(/[^a-z1-9-+ ]/g, "")
                  .replaceAll(" ", "-"),
              },
            },
          ],
        }),
      })),
    }
})

const renderedText = computed(() =>
  typeof props.blok?.text === "string"
    ? props.blok?.text
    : renderRichText(textWithHeadingIds.value, {
      resolver: (component, blok) => {
        return `<component class="not-prose small" :blok='${JSON.stringify(blok)}' is="${component}" />`
      },
    }),
)

onMounted(() => {
  if (!isEditor && location.hash) {
    setTimeout(() => {
      window.scrollTo(
        0,
        location.hash ? (document.querySelector(useRoute().hash) as HTMLElement).getBoundingClientRect().top + window.scrollY - 120 : 0,
      )
    }, 100)
  }
})

const behindHeader = computed(() => {
  function shouldReversePad(element: any) {
    switch (element.component) {
      case "spacer":
        return true
      case "asset-image":
        return true
      case "hero":
        return true
      case "hero-video":
        return true
      case "grid":
        return shouldReversePad(element.body?.at(0))
      default:
        return false
    }
  }

  return shouldReversePad
})
</script>
